import { LoadingButton } from '@mui/lab'
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, SxProps, TextField, Typography } from '@mui/material'
import React, { useState } from 'react'
import { CustomersClient } from '../../interactors/clients/CustomerClient'
import { backendClient } from '../../interactors/clients/client'
import { useAccountsStore } from '../../store/AccountsStore'
import { useGlobalSnackbarStore } from '../../store/GlobalSnackBarStore'
import { readableAxiosError } from '../../utils/axios'

interface Props {
  variant?: 'text' | 'outlined' | 'contained'
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  sx?: SxProps
  content?: React.ReactNode
  fullWidth?: boolean
  onClick?: () => void
  onConnectAttempt?: () => void
  onStripeConnectOpened?: () => void
}

export const StripeConnectButton: React.FC<Props> = ({
  variant,
  color,
  sx,
  content,
  fullWidth = true,
  onClick,
  onConnectAttempt,
  onStripeConnectOpened,
}) => {
  const [loading, setLoading] = useState(false)
  const [emailModalOpen, setEmailModalOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [emailSaving, setEmailSaving] = useState(false)

  const accountsStore = useAccountsStore()
  const globalSnackBarStore = useGlobalSnackbarStore()

  const runStripeConnect = async (stripeWindow: Window | null) => {
    setLoading(true)
    try {
      const resLink = await backendClient.get('/customers/stripe-connect-link')
      const link = resLink.data

      if (stripeWindow) {
        stripeWindow.location.assign(link)
      } else {
        window.open(link, '_blank')
      }
      onStripeConnectOpened?.()
    } catch (error) {
      stripeWindow?.close()
      console.error('Error connecting to Stripe:', error)
      globalSnackBarStore.triggerErrorMessage(
        readableAxiosError(error).join(' ') || "Impossible d'ouvrir la configuration Stripe Connect."
      )
    } finally {
      setLoading(false)
    }
  }

  const handleConnect = async () => {
    if (!accountsStore.isAdmin()) return
    if (onClick) {
      onClick()
      return
    }
    onConnectAttempt?.()
    const currentCustomer = accountsStore.connectedCustomer
    if (!currentCustomer?.email) {
      setEmail(currentCustomer?.email || '')
      setEmailError('')
      setEmailModalOpen(true)
      return
    }

    const stripeWindow = window.open('', '_blank')
    await runStripeConnect(stripeWindow)
  }

  const handleSaveEmail = async () => {
    const trimmed = email.trim()
    if (!trimmed || !/\S+@\S+\.\S+/.test(trimmed)) {
      setEmailError("L'adresse email n'est pas valide.")
      return
    }

    // Opened before the first await: a tab opened after one is no longer attributed to the click,
    // and Safari and Firefox block it.
    const stripeWindow = window.open('', '_blank')

    const currentCustomer =
      accountsStore.connectedCustomer ?? (await CustomersClient.getCurrentCustomer().catch(() => null))
    if (!currentCustomer) {
      stripeWindow?.close()
      setEmailError("Impossible de récupérer l'association.")

      return
    }

    setEmailSaving(true)
    setEmailError('')
    try {
      const updatedCustomer = await CustomersClient.editCustomer({
        ...currentCustomer,
        email: trimmed,
      })
      accountsStore.changeConnectedCustomer(updatedCustomer)
      setEmailModalOpen(false)
      await runStripeConnect(stripeWindow)
    } catch (error) {
      stripeWindow?.close()
      setEmailError("Une erreur est survenue lors de l'enregistrement.")
    } finally {
      setEmailSaving(false)
    }
  }

  return (
    <>
      <LoadingButton
        variant={variant ?? 'contained'}
        color={color ?? 'secondary'}
        fullWidth={fullWidth}
        onClick={handleConnect}
        disabled={loading || !accountsStore.isAdmin()}
        loading={loading}
        sx={sx}
      >
        {content ?? `Configurer mon Stripe Connect`}
      </LoadingButton>

      <Dialog open={emailModalOpen} onClose={() => setEmailModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adresse email de l&apos;association</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Nous avons besoin d&apos;une adresse email pour cr&eacute;er votre compte Stripe Connect.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Email de l'association"
            type="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value)
              if (emailError) {
                setEmailError('')
              }
            }}
            error={Boolean(emailError)}
            helperText={emailError}
          />
        </DialogContent>
        <DialogActions>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, width: '100%' }}>
            <LoadingButton variant="outlined" onClick={() => setEmailModalOpen(false)}>
              Annuler
            </LoadingButton>
            <LoadingButton variant="contained" loading={emailSaving} onClick={handleSaveEmail}>
              Enregistrer et continuer
            </LoadingButton>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  )
}
